import { z } from 'zod';

/**
 * API Error class
 */
export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Base API response schema
 */
const BaseResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema.nullable(),
    error: z
      .object({
        code: z.string(),
        message: z.string(),
        details: z.record(z.unknown()).optional(),
      })
      .nullable(),
    meta: z
      .object({
        requestId: z.string().optional(),
        timestamp: z.string().optional(),
      })
      .optional(),
  });

/**
 * Make an API request with Zod validation
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  dataSchema: z.ZodType<T>
): Promise<{ success: true; data: T }> {
  const response = await fetch(`/api${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });

  const json = await response.json();

  // Validate response structure
  const responseSchema = BaseResponseSchema(dataSchema);
  const parsed = responseSchema.safeParse(json);

  if (!parsed.success) {
    throw new ApiError(
      'VALIDATION_ERROR',
      'Invalid response from server',
      response.status,
      { errors: parsed.error.flatten() }
    );
  }

  if (!parsed.data.success || !parsed.data.data) {
    const error = parsed.data.error;
    throw new ApiError(
      error?.code || 'UNKNOWN_ERROR',
      error?.message || 'An unknown error occurred',
      response.status,
      error?.details
    );
  }

  return { success: true, data: parsed.data.data };
}

/**
 * GET request helper
 */
export async function apiGet<T>(
  endpoint: string,
  dataSchema: z.ZodType<T>
): Promise<{ success: true; data: T }> {
  return apiRequest(endpoint, { method: 'GET' }, dataSchema);
}

/**
 * POST request helper
 */
export async function apiPost<T>(
  endpoint: string,
  body: unknown,
  dataSchema: z.ZodType<T>
): Promise<{ success: true; data: T }> {
  return apiRequest(
    endpoint,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    dataSchema
  );
}

/**
 * PUT request helper
 */
export async function apiPut<T>(
  endpoint: string,
  body: unknown,
  dataSchema: z.ZodType<T>
): Promise<{ success: true; data: T }> {
  return apiRequest(
    endpoint,
    {
      method: 'PUT',
      body: JSON.stringify(body),
    },
    dataSchema
  );
}

/**
 * DELETE request helper
 */
export async function apiDelete<T>(
  endpoint: string,
  dataSchema: z.ZodType<T>
): Promise<{ success: true; data: T }> {
  return apiRequest(endpoint, { method: 'DELETE' }, dataSchema);
}
